import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Sparkles, Loader2, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";
import { MODE_FILTERS, BUILD_TYPES, CATEGORY_TAGS } from "@/lib/libraryConstants";

const SUPPORTED_FILE_TYPES = [".txt", ".md", ".text", ".csv", ".markdown"];

/**
 * Self-contained Import Prompts dialog. Owns its own state; notifies parent
 * via `onSuccess` after a successful save.
 */
export function ImportDialog({ open, onOpenChange, onSuccess, embedded = false }) {
  const [importText, setImportText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsedBlocks, setParsedBlocks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [evaluations, setEvaluations] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [importDirty, setImportDirty] = useState(false);

  const estimatedBlockCount = useMemo(() => {
    if (!importText.trim()) return 0;
    const byDashes = importText.split(/\n---\n/).filter((s) => s.trim());
    if (byDashes.length > 1) return byDashes.length;
    const byNumbers = importText.split(/\n\d+[\.\)]\s/).filter((s) => s.trim());
    if (byNumbers.length > 1) return byNumbers.length;
    const byDoubleNewline = importText.split(/\n\n+/).filter((s) => s.trim());
    return byDoubleNewline.length;
  }, [importText]);

  const handleParse = async () => {
    if (!importText.trim()) return;
    setParsing(true);
    setParsedBlocks(null);
    try {
      const { data } = await API.post("/library/import/parse", { raw_text: importText, build_type: "general" });
      const blocks = (data.blocks || []).map((b) => ({ ...b, build_type: b.build_type || "general", tags: b.tags || [] }));
      setParsedBlocks(blocks);
      setImportDirty(false);
      toast.success(`Parsed ${data.count} blocks`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "";
      if (detail.toLowerCase().includes("budget")) {
        toast.error("LLM budget exceeded", { description: "Go to Profile → Universal Key → Add Balance to continue using AI features.", duration: 10000 });
      } else if (e.code === "ECONNABORTED" || detail.includes("timeout")) {
        toast.error("Request timed out", { description: "The AI service is slow. Please try again." });
      } else {
        toast.error("Parsing failed", { description: detail || "Check your connection and try again." });
      }
    } finally {
      setParsing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!SUPPORTED_FILE_TYPES.includes(ext)) {
      toast.error("Unsupported file type", { description: `Only ${SUPPORTED_FILE_TYPES.join(", ")} files are supported. Got: ${ext}` });
      return;
    }
    const text = await file.text();
    setImportText(text);
    toast.success(`Loaded ${file.name}`, { description: `${text.length} characters` });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!SUPPORTED_FILE_TYPES.includes(ext)) {
      toast.error("Unsupported file type", { description: `Only ${SUPPORTED_FILE_TYPES.join(", ")} files are supported. Got: ${ext}` });
      return;
    }
    const text = await file.text();
    setImportText(text);
    toast.success(`Loaded ${file.name}`, { description: `${text.length} characters` });
  };

  const handleSave = async () => {
    if (!parsedBlocks || parsedBlocks.length === 0) return;
    setSaving(true);
    try {
      const blocksToSave = parsedBlocks.filter((_, idx) => {
        const evalItem = evaluations?.find((e) => e.index === idx);
        return !evalItem || evalItem.status !== "duplicate";
      });
      const { data } = await API.post("/library/import/save", { blocks: blocksToSave });
      toast.success(`Imported ${data.saved} blocks to library`);
      handleClose(false);
      onSuccess?.();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleEvaluate = async () => {
    if (!parsedBlocks || parsedBlocks.length === 0) return;
    setEvaluating(true);
    setEvaluations(null);
    try {
      const { data } = await API.post("/library/import/evaluate", { blocks: parsedBlocks });
      setEvaluations(data.evaluations || []);
      const optimized = (data.evaluations || []).filter((e) => e.status === "optimize" && e.optimized_content);
      if (optimized.length > 0) {
        setParsedBlocks((prev) => prev.map((b, idx) => {
          const evalItem = optimized.find((e) => e.index === idx);
          return evalItem ? { ...b, content: evalItem.optimized_content, _original_content: b.content } : b;
        }));
      }
      const stats = { keep: 0, duplicate: 0, overlap: 0, optimize: 0 };
      (data.evaluations || []).forEach((e) => { stats[e.status] = (stats[e.status] || 0) + 1; });
      toast.success("Evaluation complete", {
        description: `${stats.keep} unique, ${stats.optimize} optimized, ${stats.overlap} overlapping, ${stats.duplicate} duplicates`,
      });
    } catch (e) {
      toast.error("Evaluation failed", { description: "Blocks can still be saved without evaluation." });
    } finally {
      setEvaluating(false);
    }
  };

  const acceptOptimized = (idx) => {
    setParsedBlocks((prev) => prev.map((b, i) => {
      if (i !== idx || !b._original_content) return b;
      return { ...b, content: b._original_content, _original_content: undefined };
    }));
  };

  const updateParsedBlock = (idx, field, value) => {
    setParsedBlocks((prev) => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
    setImportDirty(true);
  };

  const removeParsedBlock = (idx) => {
    setParsedBlocks((prev) => prev.filter((_, i) => i !== idx));
    setEvaluations(null);
    setImportDirty(true);
  };

  const toggleBlockTag = (idx, tag) => {
    setParsedBlocks((prev) => prev.map((b, i) => {
      if (i !== idx) return b;
      const tags = b.tags || [];
      return { ...b, tags: tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag] };
    }));
    setImportDirty(true);
  };

  const setBulkField = (field, value) => {
    setParsedBlocks((prev) => prev.map((b) => ({ ...b, [field]: value })));
    setEvaluations(null);
    setImportDirty(true);
  };

  const handleClose = (next) => {
    if (!next && importDirty && parsedBlocks?.length > 0) {
      if (!window.confirm("Discard import? Your edits haven't been saved.")) return;
    }
    onOpenChange(next);
    if (!next) { setParsedBlocks(null); setImportText(""); setEvaluations(null); setImportDirty(false); }
  };

  const handleBack = () => {
    if (importDirty) {
      if (!window.confirm("Go back? Your block edits will be lost.")) return;
    }
    setParsedBlocks(null);
    setEvaluations(null);
    setImportDirty(false);
  };

  // Body content shared by both embedded (in LibraryAssistant) and standalone Dialog modes.
  const body = (
    <>
      {!embedded && (
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold">Import Prompts</DialogTitle>
        </DialogHeader>
      )}

      {!parsedBlocks ? (
          <div className="space-y-4 mt-2">
            <p className="text-xs text-[var(--pf-text-secondary)]">Paste text or drop a file (.txt, .md). Separate prompts with <code className="bg-[var(--pf-surface)] px-1 py-0.5 text-[var(--pf-accent)]">---</code>, numbered lists, or double newlines.</p>
            <div onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} className="relative">
              <Textarea value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={"Paste your prompts here, or drag & drop a .txt / .md file...\n---\nFirst prompt text...\n---\nSecond prompt text..."} data-testid="import-textarea"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm min-h-[200px]" rows={10} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-[11px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium cursor-pointer flex items-center gap-1" data-testid="import-file-label">
                <Upload className="w-3 h-3" />Upload file
                <input type="file" accept=".txt,.md,.text,.csv" onChange={handleFileUpload} className="hidden" data-testid="import-file-input" />
              </label>
              {importText.trim() && (
                <span className="text-[10px] text-[var(--pf-text-muted)]" data-testid="import-estimate">~{estimatedBlockCount} block{estimatedBlockCount !== 1 ? "s" : ""} detected</span>
              )}
              <div className="flex-1" />
              <Button onClick={handleParse} disabled={parsing || !importText.trim()} data-testid="import-parse-button"
                className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-9 px-4 text-xs font-medium">
                {parsing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Parsing...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Parse & Categorize</>}
              </Button>
            </div>
            {parsing && (
              <div className="flex items-center gap-3 px-4 py-3 border border-[var(--pf-border)] bg-[var(--pf-surface)]" data-testid="import-progress">
                <Loader2 className="w-4 h-4 animate-spin text-[var(--pf-accent)] shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-[var(--pf-text)]">AI is parsing and categorizing your prompts...</p>
                  <p className="text-[10px] text-[var(--pf-text-muted)] mt-0.5">Splitting text into blocks, assigning modes, and tagging categories. This usually takes 5-15 seconds.</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 mt-2" data-testid="import-preview">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[var(--pf-text)]">{parsedBlocks.length} blocks parsed</p>
              <Button variant="ghost" size="sm" onClick={handleBack} className="rounded-none text-xs text-[var(--pf-text-secondary)]">
                <ArrowLeft className="w-3 h-3 mr-1" />Back
              </Button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 border border-[var(--pf-border)] bg-[var(--pf-surface)]" data-testid="import-bulk-actions">
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--pf-text-muted)] mr-1">Set all:</span>
              <select onChange={(e) => { if (e.target.value) setBulkField("mode", e.target.value); e.target.value = ""; }} data-testid="bulk-mode"
                className="h-6 text-[10px] border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] px-1.5">
                <option value="">Mode...</option>
                {MODE_FILTERS.filter((m) => m !== "all").map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
              <select onChange={(e) => { if (e.target.value) setBulkField("build_type", e.target.value); e.target.value = ""; }} data-testid="bulk-buildtype"
                className="h-6 text-[10px] border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] px-1.5">
                <option value="">Build type...</option>
                {BUILD_TYPES.map((bt) => (<option key={bt.value} value={bt.value}>{bt.label}</option>))}
              </select>
            </div>

            {!evaluations && !evaluating && (
              <Button onClick={handleEvaluate} disabled={evaluating} data-testid="import-evaluate-button"
                variant="outline" className="w-full rounded-none border-[var(--pf-border)] h-9 text-xs hover:border-[var(--pf-accent)]">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />Evaluate Quality & Check for Duplicates
              </Button>
            )}
            {evaluating && (
              <div className="flex items-center gap-3 px-4 py-3 border border-[var(--pf-border)] bg-[var(--pf-surface)]" data-testid="eval-progress">
                <Loader2 className="w-4 h-4 animate-spin text-[var(--pf-accent)] shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-[var(--pf-text)]">Evaluating against your library...</p>
                  <p className="text-[10px] text-[var(--pf-text-muted)] mt-0.5">Checking {parsedBlocks.length} blocks for duplicates, overlaps, and optimization opportunities. This takes 10-20 seconds.</p>
                </div>
              </div>
            )}

            {evaluations && (
              <div className="flex items-center gap-3 px-3 py-2 border border-[var(--pf-border)] bg-[var(--pf-surface)] text-xs" data-testid="eval-summary">
                <span className="text-[#16A34A] font-medium">{evaluations.filter((e) => e.status === "keep").length} unique</span>
                <span className="text-[var(--pf-accent)] font-medium">{evaluations.filter((e) => e.status === "optimize").length} optimized</span>
                <span className="text-amber-600 font-medium">{evaluations.filter((e) => e.status === "overlap").length} overlapping</span>
                <span className="text-red-500 font-medium">{evaluations.filter((e) => e.status === "duplicate").length} duplicates</span>
              </div>
            )}

            <div className="space-y-3 max-h-[45vh] overflow-y-auto">
              {parsedBlocks.map((block, idx) => {
                const evalItem = evaluations?.find((e) => e.index === idx);
                const statusColor = !evalItem ? "" : evalItem.status === "keep" ? "border-l-[#16A34A]" : evalItem.status === "optimize" ? "border-l-[var(--pf-accent)]" : evalItem.status === "overlap" ? "border-l-amber-500" : "border-l-red-500";
                const isDuplicate = evalItem?.status === "duplicate";
                const contentRows = Math.min(8, Math.max(2, (block.content || "").split("\n").length + 1));
                return (
                  <div key={idx} className={`border border-[var(--pf-border)] ${statusColor ? `border-l-4 ${statusColor}` : ""} p-3 ${isDuplicate ? "opacity-40" : ""}`} data-testid={`import-preview-${idx}`}>
                    {evalItem && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[10px] px-1.5 py-0.5 font-semibold uppercase ${evalItem.status === "keep" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : evalItem.status === "optimize" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : evalItem.status === "overlap" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`} data-testid={`eval-status-${idx}`}>{evalItem.status}</span>
                        <span className="text-[10px] text-[var(--pf-text-muted)] flex-1">{evalItem.reason}</span>
                        {block._original_content && (
                          <button onClick={() => acceptOptimized(idx)} className="text-[10px] text-[var(--pf-accent)] font-medium" data-testid={`eval-revert-${idx}`}>Revert to original</button>
                        )}
                        {isDuplicate && (
                          <button onClick={() => { setEvaluations((prev) => prev.map((e) => e.index === idx ? { ...e, status: "keep", reason: "Manually kept" } : e)); }} className="text-[10px] text-[var(--pf-accent)] font-medium" data-testid={`eval-keep-${idx}`}>Keep anyway</button>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Input value={block.title} onChange={(e) => updateParsedBlock(idx, "title", e.target.value)}
                        className="rounded-none border-[var(--pf-border)] h-7 text-xs font-semibold flex-1" data-testid={`import-title-${idx}`} />
                      <select value={block.mode} onChange={(e) => updateParsedBlock(idx, "mode", e.target.value)} data-testid={`import-mode-${idx}`}
                        className="h-7 text-[11px] border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] px-2">
                        {MODE_FILTERS.filter((m) => m !== "all").map((m) => (<option key={m} value={m}>{m}</option>))}
                      </select>
                      <select value={block.build_type || "general"} onChange={(e) => updateParsedBlock(idx, "build_type", e.target.value)} data-testid={`import-buildtype-${idx}`}
                        className="h-7 text-[11px] border border-[var(--pf-border)] bg-[var(--pf-bg)] text-[var(--pf-text)] px-2">
                        {BUILD_TYPES.map((bt) => (<option key={bt.value} value={bt.value}>{bt.label}</option>))}
                      </select>
                      <button onClick={() => removeParsedBlock(idx)} className="text-red-500 hover:text-red-600 p-1" data-testid={`import-remove-${idx}`}><X className="w-3 h-3" /></button>
                    </div>
                    <textarea value={block.content} onChange={(e) => updateParsedBlock(idx, "content", e.target.value)} data-testid={`import-content-${idx}`}
                      className="w-full text-xs text-[var(--pf-text)] font-mono whitespace-pre-wrap border border-[var(--pf-border)] bg-[var(--pf-bg)] px-2 py-1.5 mb-2 resize-vertical focus:ring-2 focus:ring-[var(--pf-accent)] focus:outline-none" rows={contentRows} />
                    <div className="flex flex-wrap gap-1" data-testid={`import-tags-${idx}`}>
                      {CATEGORY_TAGS.map((tag) => {
                        const isActive = (block.tags || []).includes(tag);
                        return (
                          <button key={tag} onClick={() => toggleBlockTag(idx, tag)} type="button"
                            className={`text-[9px] px-1.5 py-0.5 transition-colors ${isActive ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-muted)] hover:bg-[var(--pf-accent)] hover:text-white"}`}>
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button onClick={handleSave} disabled={saving || parsedBlocks.length === 0} data-testid="import-save-button"
              className="w-full rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-10 font-medium">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving...</> : <>Save {evaluations ? parsedBlocks.filter((_, i) => { const e = evaluations.find((ev) => ev.index === i); return !e || e.status !== "duplicate"; }).length : parsedBlocks.length} Blocks to Library</>}
            </Button>
          </div>
        )}
    </>
  );

  if (embedded) return body;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogBody>{body}</DialogBody>
      </DialogContent>
    </Dialog>
  );
}
